import { Beer } from '../../state/types'
import { Brewery } from '../../data/breweries'

const DIRECTUS_URL = process.env.REACT_APP_DIRECTUS_URL || 'http://localhost:8055'
const DIRECTUS_TOKEN = process.env.REACT_APP_DIRECTUS_TOKEN

const getDirectusRequestInit = (): RequestInit => {
  if (!DIRECTUS_TOKEN) {
    return {}
  }

  return {
    headers: {
      Authorization: `Bearer ${DIRECTUS_TOKEN}`,
    },
  }
}

interface DirectusBeer {
  id: string
  number: number
  name: string
  brewery_id: {
    id: string
    name: string
  }
  abv: number
  style: string
  tags: string[]
  availability: 'available' | 'limited' | 'finished'
  tasting_notes: string
}

interface DirectusResponse<T> {
  data: T[]
}

const buildClientDomainKeyFilter = (clientKey?: string): string => {
  if (!clientKey) {
    return ''
  }

  return `&filter[client_id][domain_key][_eq]=${encodeURIComponent(clientKey)}`
}

const buildBeerClientDomainKeyFilter = (clientKey?: string): string => {
  if (!clientKey) {
    return ''
  }

  return `&filter[client_id][clients_id][domain_key][_eq]=${encodeURIComponent(clientKey)}`
}

const mapDirectusAvailabilityToApp = (directusAvailability: string): Beer['availability'] => {
  switch (directusAvailability) {
    case 'available':
      return 'available'
    case 'limited':
      return 'not_ready'
    case 'finished':
      return 'sold_out'
    default:
      return 'available'
  }
}

const transformDirectusBeerToAppBeer = (directusBeer: DirectusBeer): Beer => {
  return {
    id: `${directusBeer.brewery_id.name.toLowerCase().replace(/\s+/g, '-')}-${directusBeer.name
      .toLowerCase()
      .replace(/\s+/g, '-')}`,
    number: directusBeer.number,
    name: directusBeer.name,
    brewery: directusBeer.brewery_id.name,
    abv: directusBeer.abv,
    style: directusBeer.style,
    tags: directusBeer.tags || [],
    availability: mapDirectusAvailabilityToApp(directusBeer.availability),
    tastingNotes: directusBeer.tasting_notes || '',
    type: 'beer',
  }
}

interface DirectusBrewery {
  id: string
  name: string
  description: string
  location: string
  website: string
  shop_url: string
  established: number
  logo_url: string | { id: string } | null
  hero_image_url: string | { id: string } | null
}

const resolveDirectusAssetUrl = (value: string | { id: string } | null | undefined): string | undefined => {
  if (!value) {
    return undefined
  }

  const rawValue = typeof value === 'string' ? value : value.id
  if (!rawValue) {
    return undefined
  }

  if (/^https?:\/\//i.test(rawValue)) {
    return rawValue
  }

  if (rawValue.startsWith('/assets/')) {
    return `${DIRECTUS_URL}${rawValue}`
  }

  return `${DIRECTUS_URL}/assets/${rawValue}`
}

const transformDirectusBreweryToAppBrewery = (directusBrewery: DirectusBrewery): Brewery => {
  let location
  try {
    location = typeof directusBrewery.location === 'string' ? JSON.parse(directusBrewery.location) : directusBrewery.location
  } catch {
    location = {
      address: '',
      town: '',
      postcode: '',
      latitude: 0,
      longitude: 0,
    }
  }

  return {
    id: directusBrewery.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
    name: directusBrewery.name,
    description: directusBrewery.description || '',
    location,
    website: directusBrewery.website,
    shopUrl: directusBrewery.shop_url,
    established: directusBrewery.established,
    logoUrl: resolveDirectusAssetUrl(directusBrewery.logo_url),
    heroImageUrl: resolveDirectusAssetUrl(directusBrewery.hero_image_url),
  }
}

export const fetchBeersFromDirectus = async (): Promise<Beer[]> => {
  const allBeers: DirectusBeer[] = []
  let offset = 0
  const limit = 100
  let hasMore = true

  while (hasMore) {
    const url = `${DIRECTUS_URL}/items/beers?fields=*,brewery_id.id,brewery_id.name&sort=number&limit=${limit}&offset=${offset}`
    const response = await fetch(url, getDirectusRequestInit())

    if (!response.ok) {
      const responseText = await response.text().catch(() => '')
      throw new Error(
        `Directus API error: ${response.status} ${response.statusText} (GET ${url})${responseText ? `\n${responseText}` : ''}`,
      )
    }

    const result: DirectusResponse<DirectusBeer> = await response.json()

    if (result.data.length === 0) {
      hasMore = false
    } else {
      allBeers.push(...result.data)
      offset += limit

      if (result.data.length < limit) {
        hasMore = false
      }
    }
  }

  return allBeers.map(transformDirectusBeerToAppBeer)
}

export const fetchBeersFromDirectusByClientSlug = async (clientSlug: string): Promise<Beer[]> => {
  const allBeers: DirectusBeer[] = []
  let offset = 0
  const limit = 100
  let hasMore = true

  while (hasMore) {
    const url = `${DIRECTUS_URL}/items/beers?fields=*,brewery_id.id,brewery_id.name&sort=number&limit=${limit}&offset=${offset}${buildBeerClientDomainKeyFilter(clientSlug)}`
    const response = await fetch(url, getDirectusRequestInit())

    if (!response.ok) {
      const responseText = await response.text().catch(() => '')
      throw new Error(
        `Directus API error: ${response.status} ${response.statusText} (GET ${url})${responseText ? `\n${responseText}` : ''}`,
      )
    }

    const result: DirectusResponse<DirectusBeer> = await response.json()

    if (result.data.length === 0) {
      hasMore = false
    } else {
      allBeers.push(...result.data)
      offset += limit

      if (result.data.length < limit) {
        hasMore = false
      }
    }
  }

  return allBeers.map(transformDirectusBeerToAppBeer)
}

interface DirectusCider {
  id: string
  name: string
  producer_id: {
    id: string
    name: string
  } | null
  abv: number
  style: string
  sweetness: string | null
  tags: string[]
  availability: 'available' | 'limited' | 'finished'
  tasting_notes: string
}

const transformDirectusCiderToAppBeer = (directusCider: DirectusCider): Beer => {
  const producerName = directusCider.producer_id?.name ?? 'Unknown Producer'
  const tags = directusCider.tags || []
  if (directusCider.sweetness && !tags.includes(directusCider.sweetness)) {
    tags.push(directusCider.sweetness)
  }
  return {
    id: `cider-${directusCider.id}`,
    number: 0,
    name: directusCider.name,
    brewery: producerName,
    abv: directusCider.abv,
    style: directusCider.style || 'Cider',
    tags,
    availability: mapDirectusAvailabilityToApp(directusCider.availability),
    tastingNotes: directusCider.tasting_notes || '',
    type: 'cider',
  }
}

export const fetchCidersFromDirectus = async (): Promise<Beer[]> => {
  const url = `${DIRECTUS_URL}/items/ciders?fields=id,name,producer_id.id,producer_id.name,abv,style,sweetness,tags,availability,tasting_notes&sort=name&limit=-1`
  const response = await fetch(url, getDirectusRequestInit())

  if (!response.ok) {
    const responseText = await response.text().catch(() => '')
    throw new Error(
      `Directus API error: ${response.status} ${response.statusText} (GET ${url})${responseText ? `\n${responseText}` : ''}`,
    )
  }

  const result: DirectusResponse<DirectusCider> = await response.json()
  return result.data.map(transformDirectusCiderToAppBeer)
}

export const fetchCidersFromDirectusByClientSlug = async (_clientSlug: string): Promise<Beer[]> => {
  const url = `${DIRECTUS_URL}/items/ciders?fields=id,name,producer_id.id,producer_id.name,abv,style,sweetness,tags,availability,tasting_notes&sort=name&limit=-1`
  const response = await fetch(url, getDirectusRequestInit())

  if (!response.ok) {
    const responseText = await response.text().catch(() => '')
    throw new Error(
      `Directus API error: ${response.status} ${response.statusText} (GET ${url})${responseText ? `\n${responseText}` : ''}`,
    )
  }

  const result: DirectusResponse<DirectusCider> = await response.json()
  return result.data.map(transformDirectusCiderToAppBeer)
}

export const fetchBreweriesFromDirectus = async (): Promise<Brewery[]> => {
  const url = `${DIRECTUS_URL}/items/breweries?fields=*`
  const response = await fetch(url, getDirectusRequestInit())

  if (!response.ok) {
    const responseText = await response.text().catch(() => '')
    throw new Error(
      `Directus API error: ${response.status} ${response.statusText} (GET ${url})${responseText ? `\n${responseText}` : ''}`,
    )
  }

  const result: DirectusResponse<DirectusBrewery> = await response.json()

  return result.data.map(transformDirectusBreweryToAppBrewery)
}

export const fetchBreweriesFromDirectusByClientSlug = async (clientSlug: string): Promise<Brewery[]> => {
  const url = `${DIRECTUS_URL}/items/breweries?fields=*${buildClientDomainKeyFilter(clientSlug)}`
  const response = await fetch(url, getDirectusRequestInit())

  if (!response.ok) {
    const responseText = await response.text().catch(() => '')
    throw new Error(
      `Directus API error: ${response.status} ${response.statusText} (GET ${url})${responseText ? `\n${responseText}` : ''}`,
    )
  }

  const result: DirectusResponse<DirectusBrewery> = await response.json()

  return result.data.map(transformDirectusBreweryToAppBrewery)
}

export interface Sponsor {
  id: string
  name: string
  tier: 'platinum' | 'gold' | 'silver' | 'bronze'
  logo?: string
  website?: string
}

export interface Act {
  id: string
  client_id: string
  name: string
  time?: string
  description?: string
  image?: string
  website?: string
  instagram?: string
  facebook?: string
  x?: string
  tiktok?: string
  youtube?: string
  spotify?: string
}

export interface Vendor {
  id: string
  client_id: string
  name: string
  description?: string
  logo?: string
  image?: string
  menu_file?: string
  instagram?: string
  facebook?: string
}

interface DirectusSponsor {
  id: string
  name: string
  tier: string
  logo: string | null
  website: string
}

interface DirectusAct {
  id: string
  client_id: string
  name: string
  time: string | null
  description: string | null
  image: string | { id: string } | null
  website: string | null
  instagram: string | null
  facebook: string | null
  x: string | null
  tiktok: string | null
  youtube: string | null
  spotify: string | null
}

interface DirectusVendor {
  id: string
  client_id: string
  name: string
  description: string | null
  logo: string | { id: string } | null
  image: string | { id: string } | null
  menu_file: string | { id: string } | null
  instagram: string | null
  facebook: string | null
}

const transformDirectusSponsorToAppSponsor = (directusSponsor: DirectusSponsor): Sponsor => {
  const logoUrl = directusSponsor.logo ? `${DIRECTUS_URL}/assets/${directusSponsor.logo}` : undefined

  return {
    id: directusSponsor.id,
    name: directusSponsor.name,
    tier: directusSponsor.tier as Sponsor['tier'],
    logo: logoUrl,
    website: directusSponsor.website,
  }
}

const transformDirectusActToAppAct = (directusAct: DirectusAct): Act => {
  return {
    id: directusAct.id,
    client_id: directusAct.client_id,
    name: directusAct.name,
    time: directusAct.time || undefined,
    description: directusAct.description || undefined,
    image: resolveDirectusAssetUrl(directusAct.image),
    website: directusAct.website || undefined,
    instagram: directusAct.instagram || undefined,
    facebook: directusAct.facebook || undefined,
    x: directusAct.x || undefined,
    tiktok: directusAct.tiktok || undefined,
    youtube: directusAct.youtube || undefined,
    spotify: directusAct.spotify || undefined,
  }
}

const transformDirectusVendorToAppVendor = (directusVendor: DirectusVendor): Vendor => {
  return {
    id: directusVendor.id,
    client_id: directusVendor.client_id,
    name: directusVendor.name,
    description: directusVendor.description || undefined,
    logo: resolveDirectusAssetUrl(directusVendor.logo),
    image: resolveDirectusAssetUrl(directusVendor.image),
    menu_file: resolveDirectusAssetUrl(directusVendor.menu_file),
    instagram: directusVendor.instagram || undefined,
    facebook: directusVendor.facebook || undefined,
  }
}

export const fetchSponsorsFromDirectus = async (): Promise<Sponsor[]> => {
  const response = await fetch(`${DIRECTUS_URL}/items/sponsors?fields=id,name,tier,logo,website`)

  if (!response.ok) {
    throw new Error(`Directus API error: ${response.status} ${response.statusText}`)
  }

  const result: DirectusResponse<DirectusSponsor> = await response.json()

  return result.data.map(transformDirectusSponsorToAppSponsor)
}

export const fetchActsFromDirectus = async (clientId: string): Promise<Act[]> => {
  const url = `${DIRECTUS_URL}/items/acts?filter[client_id][_eq]=${encodeURIComponent(clientId)}&sort=sort,name&fields=id,client_id,name,time,description,image,website,instagram,facebook,x,tiktok,youtube,spotify`
  const response = await fetch(url, getDirectusRequestInit())

  if (!response.ok) {
    const responseText = await response.text().catch(() => '')
    throw new Error(
      `Directus API error: ${response.status} ${response.statusText} (GET ${url})${responseText ? `\n${responseText}` : ''}`,
    )
  }

  const result: DirectusResponse<DirectusAct> = await response.json()
  return result.data.map(transformDirectusActToAppAct)
}

export const fetchActsFromDirectusByClientSlug = async (clientSlug: string): Promise<Act[]> => {
  const url = `${DIRECTUS_URL}/items/acts?sort=sort,name&fields=id,client_id,name,time,description,image,website,instagram,facebook,x,tiktok,youtube,spotify${buildClientDomainKeyFilter(clientSlug)}`
  const response = await fetch(url, getDirectusRequestInit())

  if (!response.ok) {
    const responseText = await response.text().catch(() => '')
    throw new Error(
      `Directus API error: ${response.status} ${response.statusText} (GET ${url})${responseText ? `\n${responseText}` : ''}`,
    )
  }

  const result: DirectusResponse<DirectusAct> = await response.json()
  return result.data.map(transformDirectusActToAppAct)
}

export const fetchVendorsFromDirectus = async (clientId: string): Promise<Vendor[]> => {
  const url = `${DIRECTUS_URL}/items/vendors?filter[client_id][_eq]=${encodeURIComponent(clientId)}&sort=sort,name&fields=id,client_id,name,description,logo,image,menu_file,instagram,facebook`
  const response = await fetch(url, getDirectusRequestInit())

  if (!response.ok) {
    const responseText = await response.text().catch(() => '')
    throw new Error(
      `Directus API error: ${response.status} ${response.statusText} (GET ${url})${responseText ? `\n${responseText}` : ''}`,
    )
  }

  const result: DirectusResponse<DirectusVendor> = await response.json()
  return result.data.map(transformDirectusVendorToAppVendor)
}

export const fetchVendorsFromDirectusByClientSlug = async (clientSlug: string): Promise<Vendor[]> => {
  const url = `${DIRECTUS_URL}/items/vendors?sort=sort,name&fields=id,client_id,name,description,logo,image,menu_file,instagram,facebook${buildClientDomainKeyFilter(clientSlug)}`
  const response = await fetch(url, getDirectusRequestInit())

  if (!response.ok) {
    const responseText = await response.text().catch(() => '')
    throw new Error(
      `Directus API error: ${response.status} ${response.statusText} (GET ${url})${responseText ? `\n${responseText}` : ''}`,
    )
  }

  const result: DirectusResponse<DirectusVendor> = await response.json()
  return result.data.map(transformDirectusVendorToAppVendor)
}

export interface AppSettings {
  id: string
  client_id: string
  background_image?: string
  logo?: string
  primary_colour?: string
  primary_text_colour?: string
  secondary_colour?: string
  app_name: string
  is_active: boolean
  ui_theme?: 'card' | 'list'
  action_controls_mode?: 'bar' | 'fab'
  badge_position_card?: 'top_right' | 'number_column'
  badge_layout?: 'horizontal' | 'vertical'
  availability_badge_style?: 'default' | 'glass'
  brewlette?: boolean
  brewlette_result_selection_mode?: 'select_only_result' | 'keep_existing'
  event_listings?: boolean
  pint_tokens?: boolean
  pint_tokens_daily_expiry_enabled?: boolean
  event_open_sessions?: Array<{ date: string; start_time: string; end_time: string }>
  pint_tokens_pack_price_gbp_pence?: number
  pint_tokens_pack_size?: number
  ga_measurement_id?: string
}

interface DirectusAppSettings {
  id: string
  client_id: string
  background_image: string | null
  logo: string | null
  primary_colour: string | null
  primary_text_colour: string | null
  secondary_colour: string | null
  ui_theme?: 'card' | 'list' | null
  action_controls_mode?: 'bar' | 'fab' | null
  badge_position_card?: 'top_right' | 'number_column' | null
  badge_layout?: 'horizontal' | 'vertical' | null
  availability_badge_style?: 'default' | 'glass' | null
  app_name: string
  is_active: boolean
  brewlette?: boolean | null
  brewlette_result_selection_mode?: 'select_only_result' | 'keep_existing' | null
  event_listings?: boolean | null
  pint_tokens?: boolean | null
  pint_tokens_daily_expiry_enabled?: boolean | null
  event_open_sessions?: Array<{ date: string; start_time: string; end_time: string }> | null
  pint_tokens_pack_price_gbp_pence?: number | null
  pint_tokens_pack_size?: number | null
  ga_measurement_id?: string | null
}

const transformDirectusAppSettingsToApp = (directusSettings: DirectusAppSettings): AppSettings => {
  console.log('[Directus] Raw settings from API:', directusSettings);
  console.log('[Directus] ga_measurement_id value:', directusSettings.ga_measurement_id);
  
  const backgroundImageUrl = directusSettings.background_image
    ? `${DIRECTUS_URL}/assets/${directusSettings.background_image}`
    : undefined

  const logoUrl = directusSettings.logo ? `${DIRECTUS_URL}/assets/${directusSettings.logo}` : undefined

  return {
    id: directusSettings.id,
    client_id: directusSettings.client_id,
    background_image: backgroundImageUrl,
    logo: logoUrl,
    primary_colour: directusSettings.primary_colour || undefined,
    primary_text_colour: directusSettings.primary_text_colour || undefined,
    secondary_colour: directusSettings.secondary_colour || undefined,
    ui_theme: directusSettings.ui_theme || undefined,
    action_controls_mode: directusSettings.action_controls_mode || undefined,
    badge_position_card: directusSettings.badge_position_card || undefined,
    badge_layout: directusSettings.badge_layout || undefined,
    availability_badge_style: directusSettings.availability_badge_style || undefined,
    app_name: directusSettings.app_name,
    is_active: directusSettings.is_active,
    brewlette: directusSettings.brewlette ?? false,
    brewlette_result_selection_mode: directusSettings.brewlette_result_selection_mode || undefined,
    event_listings: directusSettings.event_listings ?? false,
    pint_tokens: directusSettings.pint_tokens ?? false,
    pint_tokens_daily_expiry_enabled: directusSettings.pint_tokens_daily_expiry_enabled ?? false,
    event_open_sessions: directusSettings.event_open_sessions ?? undefined,
    pint_tokens_pack_price_gbp_pence: directusSettings.pint_tokens_pack_price_gbp_pence ?? undefined,
    pint_tokens_pack_size: directusSettings.pint_tokens_pack_size ?? undefined,
    ga_measurement_id: directusSettings.ga_measurement_id || undefined,
  }
}

const isDirectusForbiddenFieldError = async (response: Response): Promise<boolean> => {
  if (response.status !== 403) {
    return false
  }

  try {
    const result = (await response.json()) as { errors?: Array<{ extensions?: { code?: string } }> }
    return Boolean(result?.errors?.some((e) => e?.extensions?.code === 'FORBIDDEN'))
  } catch {
    return true
  }
}

const fetchAppSettingsWithFields = async (url: string): Promise<AppSettings | null> => {
  const response = await fetch(url, getDirectusRequestInit())

  if (!response.ok) {
    throw response
  }

  const result: DirectusResponse<DirectusAppSettings> = await response.json()

  if (result.data.length === 0) {
    return null
  }

  return transformDirectusAppSettingsToApp(result.data[0])
}

export const fetchAppSettingsFromDirectus = async (clientId?: string): Promise<AppSettings | null> => {
  try {
    const filter = clientId
      ? `?filter[client_id][_eq]=${clientId}&filter[is_active][_eq]=true`
      : `?filter[is_active][_eq]=true&limit=1`

    const baseUrl = `${DIRECTUS_URL}/items/app_settings${filter}`
    const baseFields =
      'id,client_id,background_image,logo,primary_colour,primary_text_colour,secondary_colour,app_name,is_active,brewlette,brewlette_result_selection_mode,event_listings,pint_tokens,pint_tokens_daily_expiry_enabled,event_open_sessions,pint_tokens_pack_price_gbp_pence,pint_tokens_pack_size,ga_measurement_id'
    const fieldsWithTheme = `${baseFields},ui_theme`
    const fieldsWithUiConfig = `${fieldsWithTheme},action_controls_mode,badge_position_card,badge_layout,availability_badge_style`

    try {
      return await fetchAppSettingsWithFields(`${baseUrl}&fields=${fieldsWithUiConfig}`)
    } catch (err) {
      if (err instanceof Response) {
        const shouldFallback = await isDirectusForbiddenFieldError(err)
        if (shouldFallback) {
          return await fetchAppSettingsWithFields(`${baseUrl}&fields=${baseFields}`)
        }
        throw new Error(`Directus API error: ${err.status} ${err.statusText}`)
      }
      throw err
    }
  } catch (error) {
    console.error('[DirectusClient] Error fetching app settings:', error)
    return null
  }
}

export const fetchAppSettingsFromDirectusByClientSlug = async (clientSlug: string): Promise<AppSettings | null> => {
  try {
    const baseUrl = `${DIRECTUS_URL}/items/app_settings?filter[client_id][domain_key][_eq]=${encodeURIComponent(clientSlug)}&filter[is_active][_eq]=true&limit=1`
    const baseFields =
      'id,client_id,background_image,logo,primary_colour,primary_text_colour,secondary_colour,app_name,is_active,brewlette,brewlette_result_selection_mode,event_listings,pint_tokens,pint_tokens_daily_expiry_enabled,event_open_sessions,pint_tokens_pack_price_gbp_pence,pint_tokens_pack_size,ga_measurement_id'
    const fieldsWithTheme = `${baseFields},ui_theme`
    const fieldsWithUiConfig = `${fieldsWithTheme},action_controls_mode,badge_position_card,badge_layout,availability_badge_style`

    try {
      return await fetchAppSettingsWithFields(`${baseUrl}&fields=${fieldsWithUiConfig}`)
    } catch (err) {
      if (err instanceof Response) {
        const shouldFallback = await isDirectusForbiddenFieldError(err)
        if (shouldFallback) {
          return await fetchAppSettingsWithFields(`${baseUrl}&fields=${baseFields}`)
        }
        throw new Error(`Directus API error: ${err.status} ${err.statusText}`)
      }
      throw err
    }
  } catch (error) {
    console.error('[DirectusClient] Error fetching app settings:', error)
    return null
  }
}
